import { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

/* Mic button — records audio in the browser via MediaRecorder,
 * uploads to /api/transcribe, calls onTranscribed(text) when done. */

export function MicButton({ onTranscribed, disabled = false }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    if (recording || uploading) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Your browser doesn't support voice input");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size < 500) {
          toast.error("Hold the mic and speak — your clip was too short");
          return;
        }
        setUploading(true);
        const form = new FormData();
        form.append("file", blob, "voice.webm");
        try {
          const { data } = await api.post("/transcribe", form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 30000,
          });
          if (data?.text) onTranscribed?.(data.text);
          else toast.error("Couldn't hear anything — try again");
        } catch (e) {
          if (!e.isSubscriptionError) {
            toast.error(e?.response?.data?.detail || "Transcription failed");
          }
        } finally {
          setUploading(false);
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const stop = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      setRecording(false);
    }
  };

  const onClick = () => (recording ? stop() : start());

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || uploading}
      data-testid="mic-button"
      title={recording ? "Stop & send" : "Speak your question"}
      className={`pill-btn text-xs sm:text-sm px-3 py-2 transition-colors ${
        recording
          ? "bg-[hsl(var(--brimi-coral))] text-white animate-pulse"
          : "bg-foreground/5 hover:bg-foreground/10 text-foreground"
      } disabled:opacity-50`}
    >
      {uploading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : recording ? (
        <Square className="w-4 h-4 fill-current" />
      ) : (
        <Mic className="w-4 h-4" />
      )}
      <span className="hidden sm:inline">
        {uploading ? "Transcribing…" : recording ? "Stop" : "Speak"}
      </span>
    </button>
  );
}
